import React, { useRef, useEffect } from "react";
import "react-quill/dist/quill.snow.css";
import * as ReactQuill from "react-quill";

// Extend Quill's Image Blot to support custom styles
const Quill = (ReactQuill as any).Quill;
const ImageBlot = Quill.import("formats/image");
ImageBlot.className = "quill-full-width-image";
ImageBlot.tagName = "img";
Quill.register(ImageBlot, true);

const QuillEditor = ReactQuill as any;

interface TextEditorProps {
  formData: any;
  onChange: (value: string) => void;
  name: string;
}

const TextEditor: React.FC<TextEditorProps> = ({ formData, onChange, name }) => {
  const quillRef = useRef<any>(null);

  // Function to estimate file size from Base64 string
  const getBase64SizeInKB = (base64String: string): number => {
    const base64WithoutPrefix = base64String.split(",")[1] || base64String;
    const padding = base64WithoutPrefix.endsWith("==") ? 2 : base64WithoutPrefix.endsWith("=") ? 1 : 0;
    const sizeInBytes = (base64WithoutPrefix.length * 3) / 4 - padding;
    return sizeInBytes / 1024; // Convert to KB
  };

  // Function to compress the image to a target size without changing width/height
  const compressImageToSize = (file: File, targetSizeKB: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Canvas context not supported"));
          return;
        }

        const width = img.width;
        const height = img.height;

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.9;
        const minQuality = 0.1;
        const step = 0.1;
        let base64String = "";

        const compress = () => {
          base64String = canvas.toDataURL("image/jpeg", quality);
          const sizeKB = getBase64SizeInKB(base64String);

          if (sizeKB <= targetSizeKB || quality <= minQuality) {
            resolve(base64String);
          } else {
            quality -= step;
            setTimeout(compress, 0);
          }
        };

        compress();
      };

      img.onerror = () => reject(new Error("Image loading failed"));
      reader.onerror = () => reject(new Error("File reading failed"));

      reader.readAsDataURL(file);
    });
  };

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();

      // Function to set image width to 100%
      const setImageFullWidth = (index: number) => {
        const [imageBlot] = quill.scroll.descendant(ImageBlot, index);
        if (imageBlot && imageBlot.domNode) {
          imageBlot.domNode.setAttribute("style", "width: 100%; display: block;");
          imageBlot.domNode.classList.add("quill-full-width-image");
        }
      };

      // Handle paste event
      quill.root.addEventListener("paste", (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        if (clipboardData && clipboardData.items) {
          const items = clipboardData.items;
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") !== -1) {
              event.preventDefault();

              const file = items[i].getAsFile();
              if (file) {
                compressImageToSize(file, 200)
                  .then((compressedBase64) => {
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, "image", compressedBase64);
                    setImageFullWidth(range.index);
                    quill.insertText(range.index + 1, "\n", { align: "center" });
                    quill.insertText(range.index + 2, `${file.name}`, {
                      italic: true,
                      color: "#888",
                      align: "center",
                    });
                    quill.insertText(range.index + 2 + file.name.length, "\n", { align: "left" });
                    quill.setSelection(range.index + 3 + file.name.length);
                  })
                  .catch((error) => {
                    console.error("Image compression failed:", error);
                  });
              }
            }
          }
        }
      });

      // Custom image handler for toolbar upload
      const toolbar = quill.getModule("toolbar");
      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = () => {
          const file = input.files?.[0];
          if (file) {
            compressImageToSize(file, 200)
              .then((compressedBase64) => {
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, "image", compressedBase64);
                setImageFullWidth(range.index);
                quill.insertText(range.index + 1, "\n", { align: "center" });
                quill.insertText(range.index + 2, `${file.name}`, {
                  italic: true,
                  color: "#888",
                  align: "center",
                });
                quill.insertText(range.index + 2 + file.name.length, "\n", { align: "left" });
                quill.setSelection(range.index + 3 + file.name.length);
              })
              .catch((error) => {
                console.error("Image compression failed:", error);
              });
          }
        };
      });
    }
  }, []);

  const modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["link", "image", "video"],
      [{ color: [] }, { background: [] }], // Added color and background color options
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "align",
    "color",      // Added color format
    "background", // Added background format
  ];

  return (
    <div>
      <style>
        {`
          .quill-full-width-image {
            width: 100% !important;
            display: block !important;
          }
          .ql-editor img {
            width: 100% !important;
            display: block !important;
          }
        `}
      </style>
      <QuillEditor
        ref={quillRef}
        theme="snow"
        value={formData[name] || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        style={{
          minHeight: "200px",
          padding: "10px",
          backgroundColor: "#fff",
        }}
      />
    </div>
  );
};

export default TextEditor;